import React, { useMemo, useState } from 'react';
import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    useMediaQuery,
} from '@mui/material';

import BlockHeader from '../../common/BlockHeader';
import BlockSubHeader from '../../common/BlockSubHeader';
import ContentContainer from '../../common/ContentContainer';
import { useDataState } from '../../../providers/DataProvider.hooks';
import { AppTooltip } from '../../common/Tooltip';

import user_icon from '../../../assets/clan/clanMembers/user_icon.svg';
import pvp_icon from '../../../assets/clan/clanMembers/pvp_icon.svg';
import isle_icon from '../../../assets/clan/clanMembers/isle_icon.svg';
import wlkn_coin24 from '../../../assets/common/icons/wlkn_coin24.svg';
import { ReactComponent as SortIcon } from '../../../assets/common/sort_icon.svg';

enum SORT_VALUES {
    NAME = 'NAME',
    PVP_WINS = 'PVP_WINS',
    PVP_WIN_RATE = 'PVP_WIN_RATE',
    PVP_WLKN = 'PVP_WLKN',
    ISLE_WINS = 'ISLE_WINS',
    ISLE_WIN_RATE = 'ISLE_WIN_RATE',
    ISLE_WLKN = 'ISLE_WLKN',
}

export const ClanMemberStats = () => {
    const { clans, clanMembers, selectedClan } = useDataState();
    const [sortValue, setSortValue] = useState<SORT_VALUES>(SORT_VALUES.NAME);
    const [sortDirection, setSortDirection] = useState<boolean>(false);
    const isMobile = useMediaQuery('(max-width:1200px)');

    const selectedClanMembers = useMemo(() => {
        const onlyClanMembers = clanMembers?.data.filter((member) => member.clan === selectedClan?.clan);
        switch (sortValue) {
            case SORT_VALUES.NAME: {
                const sorted = onlyClanMembers?.sort((a, b) => a.username.localeCompare(b.username));
                return sortDirection ? sorted?.reverse() : sorted;
            }
            case SORT_VALUES.PVP_WINS: {
                const sorted = onlyClanMembers?.sort((a, b) => a.win_pvp - b.win_pvp);
                return sortDirection ? sorted : sorted?.reverse();
            }
            case SORT_VALUES.PVP_WIN_RATE: {
                const sorted = onlyClanMembers?.sort((a, b) => a.winrate_pvp - b.winrate_pvp);
                return sortDirection ? sorted : sorted?.reverse();
            }
            case SORT_VALUES.PVP_WLKN: {
                const sorted = onlyClanMembers?.sort((a, b) => a.wlkn_pvp - b.wlkn_pvp);
                return sortDirection ? sorted : sorted?.reverse();
            }
            case SORT_VALUES.ISLE_WINS: {
                const sorted = onlyClanMembers?.sort((a, b) => a.win_isl - b.win_isl);
                return sortDirection ? sorted : sorted?.reverse();
            }
            case SORT_VALUES.ISLE_WIN_RATE: {
                const sorted = onlyClanMembers?.sort((a, b) => a.winrate_isl - b.winrate_isl);
                return sortDirection ? sorted : sorted?.reverse();
            }
            case SORT_VALUES.ISLE_WLKN: {
                const sorted = onlyClanMembers?.sort((a, b) => a.wlkn_isl - b.wlkn_isl);
                return sortDirection ? sorted : sorted?.reverse();
            }
            default: {
                return onlyClanMembers;
            }
        }
    }, [clanMembers, selectedClan, sortValue, sortDirection]);

    return (
        <ContentContainer colorBg>
            <Box display={'flex'} justifyContent={'space-between'}>
                <Box>
                    <BlockHeader text="Purr-sonalities stats"></BlockHeader>
                    <BlockSubHeader
                        text="The last update of the members data was"
                        time={clans?.last_modified}
                    ></BlockSubHeader>
                </Box>
            </Box>
            <Box
                sx={{
                    borderRadius: '20px',
                    backgroundColor: '#fff',
                    display: 'flex',
                    marginBottom: '80px',
                    overflow: 'hidden',
                }}
            >
                <TableContainer
                    sx={{
                        margin: '0 10px 10px',
                    }}
                >
                    <Table
                        sx={{
                            '& th': {
                                borderBottom: 0,
                            },
                        }}
                    >
                        <TableHead>
                            <TableRow
                                sx={{
                                    '& th': {
                                        color: '#0F0E0E',
                                        fontFamily: 'AvenirNext600',
                                        fontSize: '20px',
                                        height: '88px',
                                        padding: isMobile ? '0 15px' : '0 30px',
                                    },
                                }}
                            >
                                <TableCell>
                                    <Box display={'flex'} gap={'10px'}>
                                        <img src={user_icon} alt="member"></img>
                                        Purr-sonality
                                    </Box>
                                </TableCell>
                                <TableCell colSpan={3} sx={{ borderLeft: '1px solid #ECF0F4' }}>
                                    <Box display={'flex'} gap={'10px'}>
                                        <img src={pvp_icon} alt="pvp duels"></img>PvP Duels stats{' '}
                                        <AppTooltip
                                            title={
                                                <Box
                                                    sx={{
                                                        padding: '30px',
                                                    }}
                                                >
                                                    <Typography
                                                        sx={{
                                                            color: '#0F0E0E',
                                                            fontFamily: 'AvenirNext400',
                                                            fontSize: '16px',
                                                            lineHeight: 'normal',
                                                        }}
                                                    >
                                                        The statistics of each clan member for participation in PvP
                                                        duels is updated approximately once an hour.
                                                    </Typography>
                                                    <Typography
                                                        sx={{
                                                            color: '#0F0E0E',
                                                            fontFamily: 'AvenirNext600',
                                                            fontSize: '16px',
                                                            marginTop: '20px',
                                                        }}
                                                    >
                                                        To display a user's win rate, he must play at least 100 duels
                                                    </Typography>
                                                </Box>
                                            }
                                        ></AppTooltip>
                                    </Box>
                                </TableCell>
                                <TableCell colSpan={3} sx={{ borderLeft: '1px solid #ECF0F4' }}>
                                    <Box display={'flex'} gap={'10px'} alignItems={'center'}>
                                        <img src={isle_icon} alt="isle"></img>
                                        Isle Clash stats
                                        <AppTooltip
                                            title={
                                                <Box
                                                    sx={{
                                                        padding: '30px',
                                                    }}
                                                >
                                                    <Typography
                                                        sx={{
                                                            color: '#0F0E0E',
                                                            fontFamily: 'AvenirNext400',
                                                            fontSize: '16px',
                                                            lineHeight: 'normal',
                                                        }}
                                                    >
                                                        The statistics of each clan member for participation in Isle
                                                        Clash is updated approximately once an hour.
                                                    </Typography>
                                                </Box>
                                            }
                                        ></AppTooltip>
                                    </Box>
                                </TableCell>
                            </TableRow>

                            <TableRow
                                sx={{
                                    background: '#F6F9FC',
                                    '& th': {
                                        color: '#9298A1',
                                        fontFamily: 'AvenirNext400',
                                        fontSize: '14px',
                                        height: '60px',
                                        padding: isMobile ? '0 0 0 15px' : '0 30px',
                                    },
                                    '& .selected_cell': {
                                        color: '#0F0E0E',
                                        fontFamily: 'AvenirNext500',
                                        '& svg': {
                                            path: {
                                                fill: '#0F0E0E',
                                            },
                                        },
                                    },
                                    '& div': {
                                        cursor: 'pointer',
                                        maxWidth: 'max-content',
                                        transition: 'all ease 0.1s',
                                        '&:hover': {
                                            color: '#6E7379',
                                            '& svg': {
                                                path: {
                                                    fill: '#6E7379',
                                                },
                                            },
                                        },
                                    },
                                }}
                            >
                                <TableCell className={sortValue === SORT_VALUES.NAME ? 'selected_cell' : undefined}>
                                    <Box
                                        display={'flex'}
                                        alignItems={'center'}
                                        sx={{
                                            '& svg': {
                                                transform:
                                                    sortValue === SORT_VALUES.NAME && sortDirection
                                                        ? 'rotate(180deg)'
                                                        : undefined,
                                            },
                                        }}
                                        onClick={() => {
                                            if (sortValue === SORT_VALUES.NAME) {
                                                setSortDirection(!sortDirection);
                                            } else {
                                                setSortValue(SORT_VALUES.NAME);
                                                setSortDirection(false);
                                            }
                                        }}
                                    >
                                        Purr-sonality name
                                        <SortIcon />
                                    </Box>
                                </TableCell>
                                <TableCell
                                    className={sortValue === SORT_VALUES.PVP_WINS ? 'selected_cell' : undefined}
                                    sx={{ borderLeft: '1px solid #ECF0F4' }}
                                >
                                    <Box
                                        display={'flex'}
                                        alignItems={'center'}
                                        sx={{
                                            '& svg': {
                                                transform:
                                                    sortValue === SORT_VALUES.PVP_WINS && sortDirection
                                                        ? 'rotate(180deg)'
                                                        : undefined,
                                            },
                                        }}
                                        onClick={() => {
                                            if (sortValue === SORT_VALUES.PVP_WINS) {
                                                setSortDirection(!sortDirection);
                                            } else {
                                                setSortValue(SORT_VALUES.PVP_WINS);
                                                setSortDirection(false);
                                            }
                                        }}
                                    >
                                        Wins
                                        <SortIcon />
                                    </Box>
                                </TableCell>
                                <TableCell
                                    className={sortValue === SORT_VALUES.PVP_WIN_RATE ? 'selected_cell' : undefined}
                                >
                                    <Box
                                        display={'flex'}
                                        alignItems={'center'}
                                        sx={{
                                            '& svg': {
                                                transform:
                                                    sortValue === SORT_VALUES.PVP_WIN_RATE && sortDirection
                                                        ? 'rotate(180deg)'
                                                        : undefined,
                                            },
                                        }}
                                        onClick={() => {
                                            if (sortValue === SORT_VALUES.PVP_WIN_RATE) {
                                                setSortDirection(!sortDirection);
                                            } else {
                                                setSortValue(SORT_VALUES.PVP_WIN_RATE);
                                                setSortDirection(false);
                                            }
                                        }}
                                    >
                                        Win rate
                                        <SortIcon />
                                    </Box>
                                </TableCell>
                                <TableCell className={sortValue === SORT_VALUES.PVP_WLKN ? 'selected_cell' : undefined}>
                                    <Box
                                        display={'flex'}
                                        alignItems={'center'}
                                        sx={{
                                            '& svg': {
                                                transform:
                                                    sortValue === SORT_VALUES.PVP_WLKN && sortDirection
                                                        ? 'rotate(180deg)'
                                                        : undefined,
                                            },
                                        }}
                                        onClick={() => {
                                            if (sortValue === SORT_VALUES.PVP_WLKN) {
                                                setSortDirection(!sortDirection);
                                            } else {
                                                setSortValue(SORT_VALUES.PVP_WLKN);
                                                setSortDirection(false);
                                            }
                                        }}
                                    >
                                        Earned
                                        <SortIcon />
                                    </Box>
                                </TableCell>
                                <TableCell
                                    className={sortValue === SORT_VALUES.ISLE_WINS ? 'selected_cell' : undefined}
                                    sx={{ borderLeft: '1px solid #ECF0F4' }}
                                >
                                    <Box
                                        display={'flex'}
                                        alignItems={'center'}
                                        sx={{
                                            '& svg': {
                                                transform:
                                                    sortValue === SORT_VALUES.ISLE_WINS && sortDirection
                                                        ? 'rotate(180deg)'
                                                        : undefined,
                                            },
                                        }}
                                        onClick={() => {
                                            if (sortValue === SORT_VALUES.ISLE_WINS) {
                                                setSortDirection(!sortDirection);
                                            } else {
                                                setSortValue(SORT_VALUES.ISLE_WINS);
                                                setSortDirection(false);
                                            }
                                        }}
                                    >
                                        Wins
                                        <SortIcon />
                                    </Box>
                                </TableCell>
                                <TableCell
                                    className={sortValue === SORT_VALUES.ISLE_WIN_RATE ? 'selected_cell' : undefined}
                                >
                                    <Box
                                        display={'flex'}
                                        alignItems={'center'}
                                        sx={{
                                            '& svg': {
                                                transform:
                                                    sortValue === SORT_VALUES.ISLE_WIN_RATE && sortDirection
                                                        ? 'rotate(180deg)'
                                                        : undefined,
                                            },
                                        }}
                                        onClick={() => {
                                            if (sortValue === SORT_VALUES.ISLE_WIN_RATE) {
                                                setSortDirection(!sortDirection);
                                            } else {
                                                setSortValue(SORT_VALUES.ISLE_WIN_RATE);
                                                setSortDirection(false);
                                            }
                                        }}
                                    >
                                        Win rate
                                        <SortIcon />
                                    </Box>
                                </TableCell>
                                <TableCell
                                    className={sortValue === SORT_VALUES.ISLE_WLKN ? 'selected_cell' : undefined}
                                >
                                    <Box
                                        display={'flex'}
                                        alignItems={'center'}
                                        sx={{
                                            '& svg': {
                                                transform:
                                                    sortValue === SORT_VALUES.ISLE_WLKN && sortDirection
                                                        ? 'rotate(180deg)'
                                                        : undefined,
                                            },
                                        }}
                                        onClick={() => {
                                            if (sortValue === SORT_VALUES.ISLE_WLKN) {
                                                setSortDirection(!sortDirection);
                                            } else {
                                                setSortValue(SORT_VALUES.ISLE_WLKN);
                                                setSortDirection(false);
                                            }
                                        }}
                                    >
                                        Earned
                                        <SortIcon />
                                    </Box>
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {selectedClanMembers?.map((clanMember, index) => (
                                <TableRow
                                    key={clanMember.userId}
                                    sx={{
                                        '& td': {
                                            borderBottom: 0,
                                            color: '#0F0E0E',
                                            fontFamily: 'AvenirNext500',
                                            fontSize: '16px',
                                            padding: isMobile ? '0px 15px' : '0px 30px',
                                        },
                                        '&:nth-of-type(even) td': {
                                            backgroundColor: '#F6F9FC',
                                        },
                                    }}
                                >
                                    <TableCell
                                        sx={{
                                            height: '88px',
                                        }}
                                    >
                                        <Box
                                            display={'flex'}
                                            gap={'10px'}
                                            alignItems={'center'}
                                            sx={{
                                                maxWidth: '218px',
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    height: '48px',
                                                    minWidth: '48px',
                                                    borderRadius: '50%',
                                                    border: clanMember.avatar ? 'none' : '1px solid lightgrey',
                                                    display: 'inline-block',
                                                    overflow: 'hidden',
                                                    background: `url(${clanMember.avatar})`,
                                                    backgroundSize: 'cover',
                                                    backgroundPosition: 'center',
                                                }}
                                            ></Box>
                                            <Box
                                                sx={{
                                                    whiteSpace: 'nowrap',
                                                    textOverflow: 'ellipsis',
                                                    overflow: 'hidden',
                                                }}
                                            >
                                                {clanMember.username}
                                            </Box>
                                        </Box>
                                    </TableCell>
                                    <TableCell
                                        sx={{
                                            position: 'relative',
                                            '&: after': {
                                                content: '""',
                                                background: '#ECF0F4',
                                                position: 'absolute',
                                                bottom: '20px',
                                                left: 0,
                                                height: 'calc(100% - 40px)',
                                                width: '1px',
                                            },
                                        }}
                                    >
                                        {clanMember.win_pvp}
                                    </TableCell>

                                    <TableCell>
                                        {clanMember.winrate_pvp >= 0 ? `${clanMember.winrate_pvp}%` : '-'}
                                    </TableCell>

                                    <TableCell>
                                        <Box display={'flex'} gap={'5px'}>
                                            <img src={wlkn_coin24} alt="walken coin"></img>
                                            {clanMember.wlkn_pvp.toLocaleString('en-US', {
                                                minimumFractionDigits: 2,
                                            })}
                                        </Box>
                                    </TableCell>

                                    <TableCell
                                        sx={{
                                            position: 'relative',
                                            '&: after': {
                                                content: '""',
                                                background: '#ECF0F4',
                                                position: 'absolute',
                                                bottom: '20px',
                                                left: 0,
                                                height: 'calc(100% - 40px)',
                                                width: '1px',
                                            },
                                        }}
                                    >
                                        {clanMember.win_isl}
                                    </TableCell>
                                    <TableCell>
                                        {clanMember.winrate_isl > 0 ? `${clanMember.winrate_isl}%` : '0%'}
                                    </TableCell>
                                    <TableCell>
                                        <Box display={'flex'} gap={'5px'}>
                                            <img src={wlkn_coin24} alt="walken coin"></img>
                                            {clanMember.wlkn_isl.toLocaleString('en-US', {
                                                minimumFractionDigits: 2,
                                                maximumFractionDigits: 2,
                                            })}
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>
        </ContentContainer>
    );
};
